import { FC } from 'react';
import { useOutletContext, useParams, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'usehooks-ts';
import { differenceInDays, parseISO } from 'date-fns';
import { Root } from '@radix-ui/react-dialog';

import { Container } from '@app/shared/ui/container';
import { BxGridAltIcon, CrossIcon, LogoImage } from '@app/components/ui/icons';
import { route_path } from '@app/utils/route_path';
import { cn } from '@app/app/lib';
import { StageHeaderPopover } from '@app/widgets/header/ui/modals/stage-header-popover';
import { Skeleton } from '@app/shared/ui/skeleton';
import { RenameModal } from '@app/widgets/rename-dialog';

import { Button } from '@app/shared/ui/button';
import { useTranslation, useLocalizedNavigate, removeLanguagePrefix } from '@app/app/i18n';
import { cvaIcon } from '@app/shared/ui/elements';
import { BurgerButton } from '@app/shared/ui/burger-button';
import { useLogOutMutation } from '@app/store/api/user.api';
import type { CommonOutletContext } from '@app/app/layouts';
import { useSlides } from '@app/widgets/canvas/api/hooks/useSlides';

import { useSlideStore } from '@app/widgets/canvas/store/slide';

import { useShallow } from 'zustand/react/shallow';

import { LocalizedLink, LocalizedNavLink } from '@app/shared/ui/localized-link';

import { isBoxedMode } from '@app/utils/user-status.utils';

import { screens } from '../../../../../../config/tailwind';

import { useHeaderStore } from '../model/header.store';

import { MobileMenu } from './mobile-menu';

import {
  cvaBurgerButton,
  cvaActionsButtons,
  cvaContent,
  cvaNavigation,
  cvaRoot,
  cvaPresentationName,
} from './stage-header-styles';

import { cvaLogoLink, cvaLogo, cvaMenuItem } from './header-styles';

type StageHeaderProps = {
  isWorkspaceVariant: boolean;
  isAiChatVariant?: boolean;
};

export const StageHeader: FC<StageHeaderProps> = ({ isWorkspaceVariant, isAiChatVariant }) => {
  const { t: tHeader } = useTranslation('header');
  const params = useParams();
  const location = useLocation();
  const navigate = useLocalizedNavigate();

  // Получаем текущие параметры URL для сохранения

  // Формируем query string с сохранением важных параметров

  const isMobile = useMediaQuery(`(max-width: ${screens.screens['semiMd-max'].max})`);
  const { context, commonContext } = useOutletContext<CommonOutletContext>();
  const isBoxed = isBoxedMode(commonContext);
  const { isMenuOpen, toggleHeaderMenuOpen } = useHeaderStore();
  const handleToggleMenuOpen = () => toggleHeaderMenuOpen(!isMenuOpen);
  const handleClose = () => toggleHeaderMenuOpen(false);
  const { setWorkspaceDescription, workspaceDescription } = useSlideStore(
    useShallow((state) => ({
      setWorkspaceDescription: state.setWorkspaceDescription,
      workspaceDescription: state.workspaceDescription,
    })),
  );
  const { workspaceId } = useParams();

  const [logoutQuery] = useLogOutMutation();

  const logOutHandler = () => {
    logoutQuery().then(() => {
      handleClose();
      sessionStorage.clear();
      navigate(route_path.main);
    });
  };

  const daysLeft =
    context?.product_balance &&
    context?.product_balance.is_active &&
    context?.product_balance.product.is_trial &&
    context?.product_balance.expire
      ? differenceInDays(parseISO(context?.product_balance.expire), new Date())
      : null;

  const handleRename = (newName: string) => {
    setWorkspaceDescription(newName);
  };

  const isFinalizePage = () => {
    // Check if the current path matches the workspace/:workspaceId/ route
    if (params.workspaceId) {
      const currentPath = `${route_path.workspace}${params.workspaceId}`;
      // Normalize paths by removing trailing slashes and language prefix for comparison
      const normalizedCurrentPath = currentPath.replace(/\/+$|\/$/g, '');
      const pathWithoutLang = removeLanguagePrefix(location.pathname);
      const normalizedPathname = pathWithoutLang.replace(/\/+$|\/$/g, '');
      return normalizedPathname === normalizedCurrentPath;
    }
    return false;
  };

  // Add useSlides for workspace variant
  const { isLoading: isWorkspaceSlidesLoading } = useSlides(
    isWorkspaceVariant && isFinalizePage() ? String(params.workspaceId) : undefined,
  );

  return (
    /* TODO: Replace font-inter */
    <div className={cn(cvaRoot({ isWorkspaceVariant, isAiChatVariant }), 'font-inter')}>
      <Container variant="max1920">
        <div className={cvaContent()}>
          <LocalizedLink to={route_path.workspaceCreate} className={cvaLogoLink()}>
            {isBoxed ? (
              <span className={cvaLogo()}>Sibur Roboslide</span>
            ) : (
              <LogoImage className={cvaLogo()} />
            )}
          </LocalizedLink>
          <nav className={cvaNavigation()}>
            {/* <NavLink to={route_path.projects} className={cvaNavigationItem({ isActive: false })}>
              My presentations
            </NavLink>

            <RightArrowAltIcon className={cvaArrowIcon()} />

            <NavLink
              to={route_path.textToSlides}
              className={cvaNavigationItem({ isActive: false })}
            >
              Create
            </NavLink>

            {isPptxSlides && (
              <>
                <RightArrowAltIcon className={cvaArrowIcon()} />
                <NavLink
                  to={route_path.textToSlidesPreview}
                  className={({ isActive }) => cvaNavigationItem({ isActive })}
                >
                  Upload draft
                </NavLink>
              </>
            )} */}

            {/* TODO: No route */}
            {/*<RightArrowAltIcon className={cvaArrowIcon()} />*/}
            {/*<NavLink to="" className={cvaNavigationItem({ isActive: false })}>*/}
            {/*  Input content*/}
            {/*</NavLink>*/}

            {/* TODO: No route */}
            {/*<RightArrowAltIcon className={cvaArrowIcon()} />*/}
            {/*<NavLink to="" className={cvaNavigationItem({ isActive: false })}>*/}
            {/*  Content preview*/}
            {/*</NavLink>*/}

            {/* {hasParams && (
              <>
                <RightArrowAltIcon className={cvaArrowIcon()} />
                {hasSingleTheme ? (
                  <span
                    className={cn(
                      cvaNavigationItem({ isActive: false }),
                      'opacity-50 cursor-not-allowed',
                    )}
                  >
                    Style design
                  </span>
                ) : (
                  <NavLink
                    to={
                      isWorkspace
                        ? route_path.workspaceSetupStyle +
                          String(params.workspaceId) +
                          buildSetupStyleQuery()
                        : route_path.setupStyle + String(params.projectId) + buildSetupStyleQuery()
                    }
                    className={({ isActive }) => cvaNavigationItem({ isActive })}
                  >
                    Style design
                  </NavLink>
                )}
              </>
            )}

            {hasParams && isWorkspace && (
              <>
                <RightArrowAltIcon className={cvaArrowIcon()} />
                <NavLink
                  to={route_path.workspace + String(params.workspaceId)}
                  className={({ isActive }) => cvaNavigationItem({ isActive })}
                >
                  {isPptxSlides ? 'Finalize' : 'Create & Edit slides'}
                </NavLink>
              </>
            )}

            {hasParams && !isWorkspace && isOptionInitialized && (
              <>
                <RightArrowAltIcon className={cvaArrowIcon()} />
                <NavLink
                  to={route_path.project + String(params.projectId)}
                  className={({ isActive }) => cvaNavigationItem({ isActive })}
                >
                  {isPptxSlides ? 'Finalize' : 'Create & Edit slides'}
                </NavLink>
              </>
            )} */}
          </nav>

          <div className={cvaActionsButtons()}>
            {/* Create new button */}
            <Button variant="outline" borderRadius="3xl" asChild>
              <LocalizedLink
                to={isWorkspaceVariant ? route_path.workspaceCreate : route_path.create}
              >
                {tHeader('auth.create')}
              </LocalizedLink>
            </Button>

            {/* My projects*/}
            <LocalizedNavLink
              to="/projects/"
              className={({ isActive }) =>
                cvaMenuItem({
                  isActive,
                  isTransparent: false,
                  isDotHidden: true,
                })
              }
            >
              <BxGridAltIcon className={cvaIcon()} />
              <span>{tHeader('account.myPresentations')}</span>
            </LocalizedNavLink>
          </div>

          {isMobile ? (
            <Root open={isMenuOpen}>
              <BurgerButton
                isOpen={isMenuOpen}
                activeIcon={CrossIcon}
                onClick={handleToggleMenuOpen}
                className={cvaBurgerButton()}
                ariaLabel={isMenuOpen ? tHeader('menu.close') : tHeader('menu.open')}
              />
              <MobileMenu daysLeft={daysLeft} onClose={handleClose} onLogOut={logOutHandler} />
            </Root>
          ) : (
            <StageHeaderPopover daysLeft={daysLeft} />
          )}
        </div>

        {isWorkspaceVariant && isWorkspaceSlidesLoading ? (
          <Skeleton className="relative z-[100] h-6 mb-1 max-w-[12rem] w-full" />
        ) : (
          isFinalizePage() && (
            <div className="flex items-center gap-2">
              <h2 className={cvaPresentationName()}>
                {workspaceDescription !== '' ? workspaceDescription : tHeader('account.untitled')}
              </h2>
              <RenameModal
                initialName={
                  workspaceDescription !== '' ? workspaceDescription : tHeader('account.untitled')
                }
                onRename={handleRename}
                workspaceId={workspaceId ?? ''}
                key={workspaceDescription}
              />
            </div>
          )
        )}
      </Container>
    </div>
  );
};
